import React from 'react';
import PropTypes from 'prop-types';
import Select from '@material-ui/core/Select';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import cn from 'classnames';
import startCase from 'lodash/startCase';
import find from 'lodash/find';
import get from 'lodash/get';
import Chip from '@material-ui/core/Chip';
import MenuItem from '@material-ui/core/MenuItem';
import FormHelperText from '@material-ui/core/FormHelperText';
import { makeStyles } from '@material-ui/core/styles';
import Tooltip, { InfoPropTypes } from 'components/Tooltip';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles(theme => ({
  icon: {
    color: theme.palette.text.primary,
  },
  menuItem: {
    color: theme.palette.text.primary,
  },
  menuItemSelected: {
    background: `${theme.palette.primary.main} !important`,
  },
  container: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  formControl: {
    display: 'flex',
    marginBottom: '1rem',
  },
  input: {
    color: theme.palette.text.primary,
    textAlign: 'left',
  },
  placeholder: {
    fontStyle: 'italic',
    color: theme.palette.text.primary,
  },
  chips: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  chip: {
    backgroundColor: theme.palette.background.default,
    color: theme.palette.text.primary,
    minWidth: '3rem',
    margin: 2,
  },
}));

const MultiSelect = ({
  required,
  label,
  value = ['EN'],
  onChange,
  name,
  options,
  error,
  className,
  onFocus,
  info,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const textErrorOrBool = typeof error === 'string' ? t(error) : error;
  return (
    <FormControl
      className={cn(classes.formControl, className)}
      required={required}
      error={error}
    >
      <InputLabel shrink htmlFor={name} className={classes.input}>
        {t(label)}
        {info && <Tooltip text={info} />}
      </InputLabel>
      <Select
        labelid="demo-mutiple-chip-label"
        id="demo-mutiple-chip"
        multiple
        classes={{ icon: classes.icon }}
        value={value}
        onOpen={onFocus}
        onChange={onChange}
        autoWidth={true}
        inputProps={{
          name: name,
          id: name,
        }}
        className={classes.input}
        renderValue={selected => (
          <div className={classes.chips}>
            {selected.map((value, k) => (
              <Chip
                key={k}
                label={t(get(find(options, ['id', value]), 'name'))}
                className={classes.chip}
              />
            ))}
          </div>
        )}
      >
        <MenuItem value="" disabled>
          <span className={classes.placeholder}>
            {t('Please select an option')}...
          </span>
        </MenuItem>
        {options.map((option, key) => (
          <MenuItem
            value={option.id}
            key={key}
            classes={{
              selected: classes.menuItemSelected,
              root: classes.menuItem,
            }}
          >
            <em>{t(startCase(option.name))}</em>
          </MenuItem>
        ))}
      </Select>
      {error && (
        <FormHelperText id="component-error-text">
          {textErrorOrBool}
        </FormHelperText>
      )}
    </FormControl>
  );
};

MultiSelect.propTypes = {
  className: PropTypes.string,
  required: PropTypes.bool,
  label: PropTypes.string,
  value: PropTypes.arrayOf(PropTypes.string),
  onChange: PropTypes.func.isRequired,
  onFocus: PropTypes.func.isRequired,
  name: PropTypes.string.isRequired,
  options: PropTypes.array.isRequired,
  error: PropTypes.bool,
  info: InfoPropTypes,
};

export default MultiSelect;
